/**
 * NMS INP Attribution
 * ---------------------------------------------------------------------------
 * Search Console tells us INP is failing. It does not tell us which tap, on
 * which element, or where the time went — so optimising from it is guesswork.
 * This reports every slow interaction with enough detail to act on.
 *
 * Native PerformanceObserver, no library and no extra origin: adding a CDN
 * request to diagnose a performance problem would be self-defeating.
 *
 * Pushes `nms_inp_slow_interaction` to the GTM dataLayer. Route it to GA4 as
 * a custom event and the parameters below become breakdown dimensions.
 *
 * TEMPORARY — this is diagnostic tooling. Remove it once the INP work is
 * done and the numbers are green.
 *
 * Reading the numbers:
 *   inputDelay    main thread was busy when the user tapped. Blame whatever
 *                 was running: third-party tags, a big script still parsing.
 *   processing    our own handler was slow. Blame the handler.
 *   presentation  handler finished but the next frame took a while. Usually
 *                 heavy layout, or a huge DOM the browser has to re-style.
 *
 * The largest of the three tells you where to look first.
 */

(function () {
  'use strict';

  /* Below this an interaction is not worth reporting. INP's "needs
     improvement" band starts at 200ms, so that is the floor. */
  var THRESHOLD_MS = 200;

  /* One slow tap usually means several. Cap the reports so a genuinely bad
     page cannot flood the dataLayer — the first few are enough to act on. */
  var MAX_REPORTS_PER_PAGE = 5;

  if (typeof PerformanceObserver === 'undefined') {
    return;
  }

  var supported = PerformanceObserver.supportedEntryTypes || [];

  if (supported.indexOf('event') === -1) {
    return;
  }

  var reported = 0;

  /**
   * A selector precise enough to find the element again, short enough to read
   * in a report. Falls back up the tree when the element itself is anonymous.
   */
  function describe(element) {
    if (!element || element.nodeType !== 1) {
      return '(unknown)';
    }

    var parts = [];
    var node = element;
    var depth = 0;

    while (node && node.nodeType === 1 && depth < 3) {
      var part = node.tagName.toLowerCase();

      if (node.id) {
        parts.unshift(part + '#' + node.id);
        break;
      }

      /* Data hooks name intent, so they beat presentational classes */
      var hook = null;

      for (var i = 0; i < node.attributes.length; i++) {
        var attr = node.attributes[i];

        if (attr.name.indexOf('data-') === 0 && attr.name !== 'data-testid') {
          hook = attr.name;
          break;
        }
      }

      if (hook) {
        part += '[' + hook + ']';
      } else if (node.classList.length) {
        part += '.' + node.classList[0];
      }

      parts.unshift(part);
      node = node.parentElement;
      depth++;
    }

    return parts.join(' > ');
  }

  function report(entry) {
    if (reported >= MAX_REPORTS_PER_PAGE) {
      return;
    }

    reported++;

    var inputDelay = entry.processingStart - entry.startTime;
    var processing = entry.processingEnd - entry.processingStart;
    var presentation = entry.startTime + entry.duration - entry.processingEnd;

    /* Whichever phase dominates is where the fix belongs */
    var phase = 'input_delay';

    if (processing >= inputDelay && processing >= presentation) {
      phase = 'processing';
    } else if (presentation >= inputDelay && presentation >= processing) {
      phase = 'presentation';
    }

    var connection = navigator.connection || {};

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'nms_inp_slow_interaction',
      inp_duration: Math.round(entry.duration),
      inp_event_type: entry.name,
      inp_target: describe(entry.target),
      inp_input_delay: Math.round(inputDelay),
      inp_processing: Math.round(processing),
      inp_presentation: Math.round(presentation),
      inp_worst_phase: phase,
      inp_template: document.body.getAttribute('data-template') || 'unknown',
      inp_path: window.location.pathname,
      inp_effective_type: connection.effectiveType || 'unknown',
      inp_device_memory: navigator.deviceMemory || 0,
      inp_hardware_concurrency: navigator.hardwareConcurrency || 0
    });
  }

  try {
    var observer = new PerformanceObserver(function (list) {
      var entries = list.getEntries();

      for (var i = 0; i < entries.length; i++) {
        var entry = entries[i];

        /* interactionId is only set for real user interactions. Without this
           check we would also catch events the page fired at itself. */
        if (!entry.interactionId) {
          continue;
        }

        if (entry.duration < THRESHOLD_MS) {
          continue;
        }

        report(entry);
      }
    });

    /* buffered picks up interactions that happened before this script ran —
       which is exactly when the page is slowest and taps hurt most. */
    observer.observe({
      type: 'event',
      buffered: true,
      durationThreshold: THRESHOLD_MS
    });
  } catch (error) {
    /* Diagnostics must never be the thing that breaks a page */
  }
})();
